import { Logo } from "@components/Logo";
import { PCBBackdrop } from "@components/PCBBackdrop";
import { useBillingPlan, useWallet } from "@features/billing/hooks/useBilling";
import { useOrgs } from "@features/organisations/hooks/useOrgs";
import { CreateProjectDialog } from "@features/projects/components/list/CreateProjectDialog";
import { useProjects } from "@features/projects/hooks/useProjects";
import { formatCredits } from "@models/billing";
import type { Organisation } from "@models/organisation";
import AddIcon from "@mui/icons-material/AddRounded";
import { Box, Button, Typography, alpha } from "@mui/material";
import { useOrgStore } from "@stores/orgStore";
import { useProjectStore } from "@stores/projectStore";
import { toast } from "@stores/toastStore";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { SuggestionChips } from "./SuggestionChips";

interface WelcomeScreenProps {
  onSuggestionSelect: (prompt: string) => void;
}

export function WelcomeScreen({ onSuggestionSelect }: WelcomeScreenProps) {
  const activeProjectId = useProjectStore((s) => s.activeProject);
  const activeOrgId = useOrgStore((s) => s.activeOrgId);
  // Pass the resolved scope explicitly — useProjects with `undefined` returns
  // every project the user can see across all orgs, which would hide the
  // first-run trap from a user who's an Org member but has nothing in their
  // active scope yet.
  const { data: projects, isLoading: projectsLoading } = useProjects(
    activeOrgId ?? "personal",
  );
  // Unfiltered list for name lookup — the active project may live in a
  // different scope than the current OrgSwitcher selection.
  const { data: allProjects } = useProjects();
  const activeProjectName = allProjects?.find(
    (p) => p.id === activeProjectId,
  )?.name;
  const activeProject = activeProjectId;
  const { data: orgs } = useOrgs();
  const activeOrg: Organisation | null =
    (activeOrgId && orgs?.find((o) => o.id === activeOrgId)) || null;
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);
  // First-run gate: zero projects in the current scope AND no active project
  // pinned. Don't trigger during the initial fetch — flashing the empty-state
  // CTA before the projects list loads would confuse returning users.
  const showFirstRunCta =
    !activeProject && !projectsLoading && (projects?.length ?? 0) === 0;

  // Trial credits + end date — surfaced inline on the first-run state, replacing
  // the old WelcomeModal that floated over the empty welcome screen on first
  // login. Folding it inline means trial users see one composed welcome
  // experience instead of a popup-over-empty-state stack.
  const { data: plan } = useBillingPlan();
  const { data: wallet } = useWallet();
  const trialEnd = plan?.trial_ends_at
    ? new Date(plan.trial_ends_at).toLocaleDateString(undefined, {
        month: "long",
        day: "numeric",
      })
    : null;
  const showTrialMeta = showFirstRunCta && plan?.id === "trial";

  return (
    <PCBBackdrop
      variant="welcome"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        py: 6,
        px: 2,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          // Staggered fade-up: each block (logo, eyebrow, headline,
          // prose, chips) slides into place 80ms after the last. The
          // animation runs once on mount — the welcome screen is a
          // first-impression surface, so a quick reveal earns a moment
          // of intentionality without slowing future paints.
          "& > *": {
            opacity: 0,
            animation: "cf-welcome-rise 480ms ease-out forwards",
          },
          "& > *:nth-of-type(1)": { animationDelay: "60ms" },
          "& > *:nth-of-type(2)": { animationDelay: "140ms" },
          "& > *:nth-of-type(3)": { animationDelay: "220ms" },
          "& > *:nth-of-type(4)": { animationDelay: "300ms" },
          "& > *:nth-of-type(5)": { animationDelay: "380ms" },
          "@keyframes cf-welcome-rise": {
            from: { opacity: 0, transform: "translateY(6px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        <Box sx={{ mb: 2.5 }}>
          <Logo size={44} showWordmark={false} />
        </Box>

        {/* Editorial eyebrow — pin-1 ember + status word in Geist Mono.
            Reads as a header on the active surface ("READY" with no
            project / "TRACING" once a project is selected) rather than
            as prose. The amber dot is the same brand signal the
            streaming caret uses in StreamingMessage. */}
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            mb: 2.25,
          }}
        >
          <Box
            aria-hidden
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              bgcolor: "warning.main",
              boxShadow: (t) =>
                `0 0 0 2px ${alpha(t.palette.warning.main, 0.18)}`,
            }}
          />
          <Typography
            sx={{
              fontFamily: (t) => t.fonts.mono,
              fontSize: "0.7rem",
              fontWeight: 500,
              color: "text.secondary",
              letterSpacing: "0.22em",
              textTransform: "uppercase",
            }}
          >
            {activeProject ? "Tracing" : showFirstRunCta ? "Empty" : "Ready"}
          </Typography>
        </Box>

        {activeProject ? (
          <Typography
            component="h2"
            sx={{
              color: "text.primary",
              mb: 1.25,
              fontSize: { xs: "1.5rem", sm: "1.875rem" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Querying{" "}
            <Box
              component="span"
              sx={{
                fontFamily: (t) => t.fonts.mono,
                fontWeight: 600,
                color: "primary.light",
                letterSpacing: "-0.01em",
              }}
            >
              ‘{activeProjectName ?? activeProjectId}’
            </Box>
            <Box
              component="span"
              sx={{
                color: "warning.main",
                textShadow: (t) =>
                  `0 0 14px ${alpha(t.palette.warning.light, 0.6)}`,
              }}
            >
              .
            </Box>
          </Typography>
        ) : showFirstRunCta ? (
          <Typography
            component="h2"
            sx={{
              color: "text.primary",
              mb: 1.25,
              fontSize: { xs: "1.5rem", sm: "1.875rem" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Bring in your first board
            <Box
              component="span"
              sx={{
                color: "warning.main",
                textShadow: (t) =>
                  `0 0 14px ${alpha(t.palette.warning.light, 0.6)}`,
              }}
            >
              .
            </Box>
          </Typography>
        ) : (
          <Typography
            component="h2"
            sx={{
              color: "text.primary",
              mb: 1.25,
              fontSize: { xs: "1.5rem", sm: "1.875rem" },
              fontWeight: 700,
              letterSpacing: "-0.02em",
              lineHeight: 1.15,
            }}
          >
            Ask the schematic
            <Box
              component="span"
              sx={{
                color: "warning.main",
                textShadow: (t) =>
                  `0 0 14px ${alpha(t.palette.warning.light, 0.6)}`,
              }}
            >
              .
            </Box>
          </Typography>
        )}

        <Typography
          sx={{
            color: "text.secondary",
            fontSize: "0.9375rem",
            maxWidth: 520,
            lineHeight: 1.6,
            mx: "auto",
          }}
        >
          {activeProject
            ? "Topology, datasheets, BOMs, firmware stubs — grounded in your real netlist."
            : showFirstRunCta
              ? "Spin up an empty project to start chatting through a design, or drop a netlist later. Either way the chat stays grounded in real components and nets."
              : "Pick a project from the sidebar, then ask anything: topology, datasheets, BOMs, firmware stubs."}
        </Typography>

        {showFirstRunCta ? (
          <Box
            sx={{
              mt: 3,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 1.5,
              // Match SuggestionChips' staggered entry so the swap reads as
              // a single composed reveal rather than two competing animations.
              opacity: 0,
              animation: "cf-chip-rise 380ms ease-out forwards",
              animationDelay: "440ms",
              "@keyframes cf-chip-rise": {
                from: { opacity: 0, transform: "translateY(6px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => setCreateOpen(true)}
              sx={{
                px: 3,
                py: 1.25,
                fontSize: "0.95rem",
                fontWeight: 600,
                letterSpacing: "-0.005em",
                textTransform: "none",
                borderRadius: 1.5,
                boxShadow: (t) =>
                  `0 0 0 1px ${alpha(t.palette.primary.main, 0.4)}, 0 4px 18px -8px ${alpha(t.palette.primary.main, 0.6)}`,
                "&:hover": {
                  boxShadow: (t) =>
                    `0 0 0 1px ${alpha(t.palette.primary.main, 0.6)}, 0 6px 22px -8px ${alpha(t.palette.primary.main, 0.8)}`,
                },
              }}
            >
              Create your first project
            </Button>

            {showTrialMeta && (
              <Typography
                sx={{
                  fontFamily: (t) => t.fonts.mono,
                  fontSize: "0.7rem",
                  fontWeight: 500,
                  color: "text.disabled",
                  letterSpacing: "0.12em",
                  textTransform: "uppercase",
                }}
              >
                {formatCredits(wallet?.balance ?? 500)} trial credits
                {trialEnd ? ` · ends ${trialEnd}` : ""}
              </Typography>
            )}
          </Box>
        ) : (
          <SuggestionChips onSelect={onSuggestionSelect} />
        )}
      </Box>

      <CreateProjectDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(projectId) => {
          toast.success(
            activeOrg
              ? `Created project in ${activeOrg.name}.`
              : "Created project.",
          );
          void navigate({
            to: "/projects/$projectId/overview",
            params: { projectId },
          });
        }}
        activeOrg={activeOrg}
      />
    </PCBBackdrop>
  );
}
